import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { Modal, Typography, Form, Space, Button, DatePicker } from "antd";
import moment from "moment";
import { useEffect } from "react";

import { usePatchInsight } from "reactQuery/hooks/useInsights";
import { opportunityKeys } from "reactQuery/keys";
import { useSnackStore, useModalStore, ModalNames } from "stores/zustandStore";

export const EditDisplayDateModal = () => {
    const displaySuccessSnack = useSnackStore((state) => state.displaySuccessSnack);
    const displayErrorSnack = useSnackStore((state) => state.displayErrorSnack);
    const setModalHidden = useModalStore((state) => state.setModalHidden);
    const openModal = useModalStore((state) => state.openModal);
    const modalInitialValues = useModalStore((state) => state.modalInitialValues);

    const patchInsightQuery = usePatchInsight();
    const queryClient = useQueryClient();

    const [form] = Form.useForm();
    const formId = "edit-display-date-modal-form";

    useEffect(() => {
        if (openModal === ModalNames.EditDisplayDate) {
            form.setFieldValue(
                "displayDate",
                moment(modalInitialValues.insight.display_date)
            );
        }
    }, [modalInitialValues]);

    const isRefetchingOpportunity = Boolean(
        useIsFetching({
            queryKey: opportunityKeys.single_opportunity(
                modalInitialValues?.insight?.opportunity?.id
            ),
        })
    );

    const onModalOk = () => {
        const payload = {
            display_date: form.getFieldValue("displayDate").format("YYYY-MM-DD"),
        };
        patchInsightQuery.mutate(
            { payload: payload, insightId: modalInitialValues.insight.id },
            {
                onSuccess: () => {
                    queryClient
                        .invalidateQueries(
                            opportunityKeys.single_opportunity(
                                modalInitialValues.insight.opportunity.id
                            ),
                            { exact: true }
                        )
                        .then(() => {
                            setModalHidden();
                            displaySuccessSnack({
                                message: "Display date updated successfully",
                            });
                        });
                },
                onError: () => {
                    setModalHidden();
                    displayErrorSnack({
                        message: "Error updating display date",
                    });
                },
            }
        );
    };
    const onModalCancel = () => {
        setModalHidden();
        form.resetFields();
    };

    return (
        <Modal
            open={openModal === ModalNames.EditDisplayDate}
            centered
            forceRender
            destroyOnClose={true}
            onCancel={onModalCancel}
            footer={null}
        >
            <Form
                id={formId}
                form={form}
                name={formId}
                layout="vertical"
                style={{ width: "80%", margin: "auto" }}
                preserve={false}
                onFinish={onModalOk}
            >
                <div style={{ textAlign: "center", marginBottom: "25px" }}>
                    <Space>
                        <Typography.Title level={3} style={{ margin: 0 }}>
                            Edit Display Date
                        </Typography.Title>
                    </Space>
                </div>
                <Form.Item
                    name="displayDate"
                    label="Display Date"
                    rules={[
                        {
                            required: true,
                            message: "Please enter a display date!",
                        },
                    ]}
                    preserve={false}
                >
                    <DatePicker
                        style={{ width: "100%" }}
                        format="YYYY-MM-DD"
                        showTime={false}
                        data-testid="display-date-picker"
                        placeholder="Select display date"
                    />
                </Form.Item>
                <Form.Item>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                        }}
                    >
                        <Button key="submit" type="default" onClick={onModalCancel}>
                            Cancel
                        </Button>
                        <Button
                            key="link"
                            type="primary"
                            htmlType="submit"
                            style={{ marginLeft: "10px" }}
                            loading={
                                patchInsightQuery.isLoading || isRefetchingOpportunity
                            }
                        >
                            Submit
                        </Button>
                    </div>
                </Form.Item>
            </Form>
        </Modal>
    );
};
